/**
 * The live emergency call, as a corner panel rather than a full-screen takeover.
 *
 * A takeover is right for the alarm — it demands an answer. It is wrong for the call that follows:
 * the operator on a call needs to look at the map to see where the wearer is, at the roster, at the
 * camera. So this floats above the app and lets them work while they talk.
 */

import { Mic, MicOff, PhoneOff, X } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { callIsActive, callStatusCopy } from './call-copy'
import { CallSettingsMenu } from './CallSettingsMenu'
import { claimSosCallDial, setSosCallPhase } from './sos-call-store'
import { useDraggablePanel } from './useDraggablePanel'
import { useHelmetCall } from './useHelmetCall'
import { useMicLevel } from './useMicLevel'

/**
 * How long to wait for the command channel before placing the call anyway.
 *
 * Generous relative to a socket that normally subscribes in well under a second, because the
 * cost of the two outcomes is not symmetric: waiting a moment longer on a channel that is coming
 * up costs nothing an operator would notice, while starting too early costs the call outright.
 */
const CHANNEL_WAIT_MS = 8_000

export interface SosCallPanelProps {
  /** Identifies THIS call, so a late report cannot settle the one that replaced it. */
  callId: string
  robotId: string
  /** What to call the wearer on screen. */
  label: string
  /** Called when the operator is finished with the panel — hung up, or dismissed a dead call. */
  onClose: () => void
}

export function SosCallPanel({ callId, robotId, label, onClose }: SosCallPanelProps) {
  const { t } = useTranslation('console')
  const { phase, remoteStream, micOn, toggleMic, switchMic, micGain, setMicGain, start, hangUp, connected, micStream } =
    useHelmetCall({ robotId })
  const videoRef = useRef<HTMLVideoElement | null>(null)
  // The panel floats over the whole console so the operator can keep working — which means the
  // corner it starts in is the wrong corner about half the time (it lands on the map, the roster,
  // or the camera tile they are reading). Dragged by its header, snapped to a side on release.
  const drag = useDraggablePanel()
  const startedRef = useRef(false)

  // Placing the call is what this panel is FOR, so it starts itself rather than offering another
  // button — the operator already pressed Accept. Guarded by a ref so React's development
  // double-mount does not ring the helmet twice.
  //
  // It waits for `connected` instead of firing on the first commit, and that wait is the whole
  // difference between a call and no call. This hook opens its own WebSocket in an effect, so on
  // the commit where the panel mounts the channel is ALWAYS `disconnected`; starting there hits
  // `start`'s own channel guard and fails instantly with "Lost the connection to the helmet." The
  // ref makes that one-shot, the failed panel offers only Dismiss, and the emergency has already
  // been acknowledged — so the wearer of a fall alarm would simply never be called, with no way to
  // redial. Bounded, because a channel that never comes up must still report rather than sit
  // silent: after the wait we start anyway and let the guard fail it honestly.
  //
  // The latch lives in the STORE, keyed by callId — a ref only guards React's development
  // double-mount and cannot survive this component being rebuilt. The overlay is mounted in
  // `AppLayout`, which logout, an error boundary, or a route outside the layout all unmount, and a
  // fresh panel over the same stored call would ring the helmet again for an emergency handled long
  // ago, calling `getUserMedia` outside any user gesture while doing it.
  useEffect(() => {
    if (startedRef.current) return

    const dial = () => {
      if (startedRef.current) return
      startedRef.current = true
      if (claimSosCallDial(callId)) start()
    }

    if (connected) {
      dial()

      return
    }

    const timer = setTimeout(dial, CHANNEL_WAIT_MS)

    return () => clearTimeout(timer)
  }, [callId, connected, start])

  // The store decides whether another emergency may take the line, and it decides from THIS. Pushed
  // rather than guessed: only this hook knows the call has failed, and until it says so the store
  // must keep treating the call as live.
  useEffect(() => {
    setSosCallPhase(callId, phase)
  }, [callId, phase])

  // srcObject is a property, not an attribute, so it cannot be set through JSX.
  useEffect(() => {
    const el = videoRef.current

    if (el && el.srcObject !== remoteStream) el.srcObject = remoteStream
  }, [remoteStream])

  const status = callStatusCopy(phase)
  const active = callIsActive(phase)
  // Only while the operator believes they are transmitting: a muted mic is silent on purpose and
  // the button already says so.
  const { peak: micPeak, silent: micSilent } = useMicLevel(micStream, active)

  return (
    <div
      // A LIVE call sits ABOVE the emergency modal's z-[100]; a finished one stays below it.
      //
      // This reverses an earlier decision, deliberately. The modal is a full-screen z-[100]
      // backdrop, so while it is up an operator MID-CONVERSATION with someone in distress loses
      // Mute and Hang up until they answer the new alarm — and Escape is deliberately swallowed.
      // Taking the controls away from a live emergency call is worse than a small corner panel
      // over the new alarm, which still covers the rest of the screen and still demands an answer.
      // `fixed` with no corner classes: the position is inline, from the drag hook, which starts it
      // in the same bottom-right corner it has always used.
      className={`fixed ${active ? 'z-[110]' : 'z-[90]'} w-80 overflow-hidden rounded-xl border border-red-500/60 bg-gray-900 text-gray-100 shadow-2xl`}
      style={drag.style}
      ref={drag.panelRef}
      role="dialog"
      aria-label={t('alerts.sos.call.title', 'Emergency call')}
      data-testid="sos-call-panel"
    >
      {/* The HEADER is the handle, not the whole panel: dragging from the body would make Mute and
          Hang up unreliable, and those two are the controls that matter on an emergency call. */}
      <div
        {...drag.handleProps}
        className="flex items-center gap-2 bg-red-700 px-3 py-2"
        data-testid="sos-call-drag-handle"
      >
        <span className="min-w-0 flex-1 truncate text-sm font-bold" title={robotId}>
          {t('alerts.sos.call.with', 'Calling {{name}}', { name: label })}
        </span>
        {!active && (
          <button
            type="button"
            onClick={onClose}
            aria-label={t('alerts.sos.call.dismiss', 'Dismiss')}
            data-testid="sos-call-dismiss"
            className="rounded p-1 hover:bg-red-600"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        )}
      </div>

      {/* The wearer's camera. Kept mounted through `ringing` so the first frame appears without a
          layout jump, and `playsInline` because iOS otherwise takes the video fullscreen. */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        // NOT muted: this is the wearer's voice, and it is the entire point of the call.
        className="aspect-video w-full bg-black object-contain"
        data-testid="sos-call-video"
      />

      <p
        className={`px-3 py-2 text-sm ${phase.kind === 'failed' ? 'text-red-300' : 'text-gray-300'}`}
        role="status"
        data-testid="sos-call-status"
      >
        {t(`alerts.sos.call.${status.key}`, status.fallback)}
        {phase.kind === 'failed' && phase.detail ? (
          <span className="block text-xs text-gray-400">{phase.detail}</span>
        ) : null}
      </p>

      {/* A call can be connected, publishing opus, and read by the helmet, and the wearer still hears
          nothing — because the track macOS handed the browser is silent. Measured on a real call:
          27 ms round trip, zero audio energy, for its whole duration. Nothing in WebRTC calls that
          an error, so the operator found out by asking the wearer. */}
      {active && micOn && micSilent && (
        <p
          className="border-t border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-300"
          role="status"
          data-testid="sos-call-mic-silent"
        >
          {t(
            'alerts.sos.call.micSilent',
            'Your microphone is sending silence — the wearer cannot hear you. Check the input device in your system settings.',
          )}
        </p>
      )}

      {active && (
        <div className="flex items-center gap-2 border-t border-white/10 px-3 py-2">
          <button
            type="button"
            onClick={toggleMic}
            data-testid="sos-call-mic"
            aria-pressed={!micOn}
            className="flex items-center gap-1.5 rounded-md bg-gray-800 px-3 py-1.5 text-xs font-semibold text-gray-100 hover:bg-gray-700"
          >
            {micOn ? <Mic className="h-4 w-4" aria-hidden /> : <MicOff className="h-4 w-4" aria-hidden />}
            {micOn ? t('alerts.sos.call.mute', 'Mute') : t('alerts.sos.call.unmute', 'Unmute')}
          </button>
          {/* One icon rather than a row: the panel overlays the console, so height it does not need
              is map the operator cannot see. Amber when the mic is silent — the banner says what is
              wrong, the gear says where the fix is. */}
          <CallSettingsMenu
            onSelectMic={switchMic}
            urgent={micOn && micSilent}
            peak={micPeak}
            muted={!micOn}
            gain={micGain}
            onGainChange={setMicGain}
          />
          {/* Said out loud rather than left to the icon: a muted mic during an emergency is a
              silence the operator must never mistake for the wearer not replying. */}
          {!micOn && (
            <span className="text-xs text-amber-300" data-testid="sos-call-muted">
              {t('alerts.sos.call.muted', 'Your microphone is off')}
            </span>
          )}
          <button
            type="button"
            onClick={() => {
              hangUp()
              onClose()
            }}
            data-testid="sos-call-hangup"
            className="ml-auto flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-500"
          >
            <PhoneOff className="h-4 w-4" aria-hidden />
            {t('alerts.sos.call.hangUp', 'Hang up')}
          </button>
        </div>
      )}
    </div>
  )
}
